import type { LevelDef } from '../level.js';
import type { GameState } from '../types.js';
import { carsOf } from './cars.js';
import { emergencyLost, emergencyUnderWay } from './emergency.js';

/**
 * Decide whether the run has ended.
 *
 * v0.2 adds a third way to finish: the objective becoming *unreachable*. Once
 * enough passengers have walked away that the target can no longer be met, the
 * run ends there and says so, rather than making the player play out a minute
 * they have already lost. "I know what I did wrong" is a design pillar, and it
 * only holds if the moment a run is lost is the moment it stops.
 */

/** Passengers who could still be delivered: waiting, riding, or not yet arrived. */
function stillServable(state: GameState): number {
  return state.passengers.filter(
    (p) => p.state === 'unspawned' || p.state === 'waiting' || p.state === 'riding',
  ).length;
}

/** Everyone lost, however they were lost. */
function lost(state: GameState): number {
  return state.passengers.filter((p) => p.state === 'abandoned').length;
}

/** How many the objective still needs, or null when the shape has no quota. */
function quotaFor(objective: LevelDef['objective'], state: GameState): number | null {
  switch (objective.kind) {
    case 'deliverAll':
    case 'withinTrips':
      return state.passengers.length;
    case 'deliverN':
    case 'serviceAbove':
      return objective.count;
    case 'survive':
      return null;
  }
}

/**
 * What the player is working toward, in the terms they should see it.
 *
 * The HUD used to show `delivered / everyone who exists`, which is not the goal
 * on any level that sets a quota — and every level from 14 on sets one at about
 * four fifths of the roster. A playtester read "13 of 15", concluded they had
 * dropped two people, and was looking at a perfect run. The number on screen has
 * to be the number being graded, and that number is a rule, so it lives here.
 */
export type ObjectiveStatus =
  | {
      readonly kind: 'deliveries';
      readonly done: number;
      readonly needed: number;
      /** Trips still allowed on a puzzle shift (ADR-0037); null on any other. */
      readonly tripsLeft: number | null;
    }
  | {
      readonly kind: 'shift';
      readonly remainingMs: number;
      readonly lost: number;
      readonly allowed: number;
    };

export function objectiveStatus(
  state: GameState,
  objective: LevelDef['objective'],
): ObjectiveStatus {
  if (objective.kind === 'survive') {
    return {
      kind: 'shift',
      remainingMs: Math.max(0, objective.untilMs - state.elapsedMs),
      lost: lost(state),
      allowed: objective.maxLost,
    };
  }
  return {
    kind: 'deliveries',
    done: state.score.delivered,
    needed: quotaFor(objective, state) ?? state.passengers.length,
    tripsLeft:
      objective.kind === 'withinTrips' ? Math.max(0, objective.trips - state.score.trips) : null,
  };
}

/**
 * How many more people can be lost before the run is lost.
 *
 * Zero means the next loss ends it: always so on a `deliverAll` level, and on
 * any level once its slack is spent. The player should know that before it
 * happens, not from the results screen, so the view asks here rather than
 * working it out.
 */
export function lossesToSpare(state: GameState, objective: LevelDef['objective']): number {
  if (objective.kind === 'survive') return Math.max(0, objective.maxLost - lost(state));
  const quota = quotaFor(objective, state) ?? state.passengers.length;
  return Math.max(0, state.score.delivered + stillServable(state) - quota);
}

/** Nobody left to arrive, board or ride: the building is empty. */
function buildingEmpty(state: GameState): boolean {
  return stillServable(state) === 0;
}

function objectiveMet(state: GameState, objective: LevelDef['objective']): boolean {
  switch (objective.kind) {
    case 'deliverAll':
    case 'withinTrips':
      // An empty roster is a malformed level, not a win. `createInitialState`
      // rejects it up front; this guard keeps the rule honest either way.
      return state.passengers.length > 0 && state.passengers.every((p) => p.state === 'delivered');
    case 'deliverN':
      return state.score.delivered >= objective.count;
    case 'serviceAbove':
      return (
        state.score.delivered >= objective.count && state.score.service >= objective.minService
      );
    case 'survive':
      // Either the shift runs out, or the building does. Clearing everyone early
      // *is* surviving the rush, and making the player watch an empty building
      // for the remaining ten seconds teaches them the clock is the game — a
      // playtester sat through exactly that on three separate levels.
      return (
        (state.elapsedMs >= objective.untilMs || buildingEmpty(state)) &&
        lost(state) <= objective.maxLost
      );
  }
}

/**
 * Anyone still aboard the cabin, mid-trip.
 *
 * A `deliverN` level is allowed to end with people never collected — that is
 * the whole mechanic, a level spawning more demand than the quota requires,
 * and it reads as "you didn't get to everyone," which is fair. Someone still
 * *riding* is different: the player already boarded them, already accepted
 * the trip, and ending mid-ride strands them where the car happens to be —
 * not delivered, not abandoned, just frozen. A playtest session hit this on
 * two levels where the quota completed while two or three passengers were
 * still riding toward a floor the car had not reached yet, and it read
 * exactly as it was: unfinished.
 */
function anyoneStillRiding(state: GameState): boolean {
  return carsOf(state).some((car) => car.riders.length > 0);
}

export function evaluateObjective(state: GameState, objective: LevelDef['objective']): void {
  if (state.status !== 'playing') return;

  // An emergency lost ends the shift there and then (ADR-0029), whatever else
  // was going well: that is the promise the clock on it makes.
  if (emergencyLost(state)) {
    state.status = 'lost';
    state.endReason = 'emergency';
    return;
  }

  if (objectiveMet(state, objective)) {
    // The goal is satisfied either way. Once it is, no later tick can turn
    // this into a loss — a mission already accomplished does not expire
    // because the last drop-off took a few more seconds than the clock had
    // left, so every other check below is skipped rather than merely delayed.
    // Nor in the middle of an emergency: the crew is still in the building.
    if (!anyoneStillRiding(state) && !emergencyUnderWay(state)) {
      state.status = 'won';
      state.endReason = 'objective-complete';
    }
    return;
  }

  // Losing more than the shift allows ends it there — waiting out the clock
  // when the outcome is already decided teaches nothing.
  if (objective.kind === 'survive') {
    if (lost(state) > objective.maxLost) {
      state.status = 'lost';
      state.endReason = 'too-many-lost';
    }
    return;
  }

  // A puzzle shift (ADR-0037): a trip past the limit ends it, and so does the
  // last one allowed coming to rest with someone still to take.
  if (objective.kind === 'withinTrips' && outOfTrips(state, objective.trips)) {
    state.status = 'lost';
    state.endReason = 'out-of-trips';
    return;
  }

  const target = quotaFor(objective, state);
  if (target !== null && state.score.delivered + stillServable(state) < target) {
    state.status = 'lost';
    state.endReason = 'objective-impossible';
    return;
  }

  // Nobody left to arrive, board or ride, and the goal still unmet: nothing the
  // player does from here can meet it. A shift with enough people home but too
  // few points waited for its time limit in an empty building, and the player
  // left it three times (PLAYTEST_LOG, 2026-10-21).
  if (buildingEmpty(state)) {
    state.status = 'lost';
    state.endReason = 'objective-impossible';
    return;
  }

  if (state.elapsedMs >= state.timeLimitMs) {
    state.status = 'lost';
    state.endReason = 'out-of-time';
  }
}

/** Past the trip limit, or at it with every car standing shut. */
function outOfTrips(state: GameState, limit: number): boolean {
  if (state.score.trips > limit) return true;
  return (
    state.score.trips >= limit &&
    carsOf(state).every((car) => car.phase === 'idle' && car.doorPhase === 'closed')
  );
}

/** 3, 2 or 1 stars. Only meaningful once the run is won. */
export function starsFor(state: GameState): 0 | 1 | 2 | 3 {
  if (state.status !== 'won') return 0;

  // Service-graded levels rank high-to-low; time-graded levels low-to-high.
  if (state.stars.metric === 'service') {
    if (state.score.service >= state.stars.three) return 3;
    if (state.score.service >= state.stars.two) return 2;
    return 1;
  }
  if (state.stars.metric === 'trips') {
    if (state.score.trips <= state.stars.three) return 3;
    if (state.score.trips <= state.stars.two) return 2;
    return 1;
  }

  if (state.elapsedMs <= state.stars.three) return 3;
  if (state.elapsedMs <= state.stars.two) return 2;
  return 1;
}

/** The number a level is actually graded on, for HUD and results. */
export function gradedValue(state: GameState): number {
  if (state.stars.metric === 'service') return state.score.service;
  return state.stars.metric === 'trips' ? state.score.trips : state.elapsedMs;
}
